import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { SettingsProvider } from "./context/SettingsContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Navbar } from "./components/layout/Navbar";
import { Footer } from "./components/layout/Footer";
import { MobileBar } from "./components/layout/MobileBar";
import { CookieBanner } from "./components/layout/CookieBanner";
import { SEO } from "./components/shared/SEO";
import Home from "./pages/Home";
import ChiSiamo from "./pages/ChiSiamo";
import CosaFacciamo from "./pages/CosaFacciamo";
import Progetti from "./pages/Progetti";
import ProgettoDetail from "./pages/ProgettoDetail";
import Investitori from "./pages/Investitori";
import Operazioni from "./pages/Operazioni";
import OperazioneDetail from "./pages/OperazioneDetail";
import ProponiImmobile from "./pages/ProponiImmobile";
import Contatti from "./pages/Contatti";
import Legal from "./pages/Legal";
import RottaDelValore from "./pages/RottaDelValore";
import DiarioDiBordo, { DiarioPost } from "./pages/DiarioDiBordo";
import BookAdmin from "./pages/admin/BookAdmin";
import PostsAdmin, { PostForm } from "./pages/admin/PostsAdmin";
import MediaAdmin from "./pages/admin/MediaAdmin";
import InstagramAdmin from "./pages/admin/InstagramAdmin";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import ProjectsAdmin from "./pages/admin/ProjectsAdmin";
import ProjectForm from "./pages/admin/ProjectForm";
import OperationsAdmin from "./pages/admin/OperationsAdmin";
import OperationForm from "./pages/admin/OperationForm";
import LeadsAdmin from "./pages/admin/LeadsAdmin";
import SettingsAdmin from "./pages/admin/SettingsAdmin";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) return el.scrollIntoView({ behavior: "smooth" });
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
};

const PublicLayout = () => (
  <>
    <Navbar />
    <main className="pb-16 md:pb-0">
      <Outlet />
    </main>
    <Footer />
    <MobileBar />
    <CookieBanner />
  </>
);

const Protected = () => {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-stone text-sm tracking-widest uppercase">Caricamento…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return <AdminLayout />;
};

const NotFound = () => (
  <section className="min-h-[70vh] flex items-center bg-ink text-cream">
    <SEO title="Pagina non trovata | Home Haven" description="La pagina richiesta non esiste. Torna alla homepage di Home Haven o scopri i progetti." robots="noindex, follow" />
    <div className="container-hh pt-32">
      <div className="eyebrow mb-4">404</div>
      <h1 className="h-display">Pagina non trovata</h1>
      <p className="lead !text-cream/70 mt-6 max-w-xl">La pagina richiesta non esiste o è stata spostata.</p>
      <div className="flex flex-wrap gap-4 mt-10">
        <a href="/" className="btn-light">Torna alla home</a>
        <a href="/progetti" className="btn-outline-light">Scopri i progetti</a>
        <a href="/contatti" className="btn-outline-light">Contatti</a>
      </div>
    </div>
  </section>
);

function App() {
  return (
    <div className="App">
      <SettingsProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/chi-siamo" element={<ChiSiamo />} />
                <Route path="/cosa-facciamo" element={<CosaFacciamo />} />
                <Route path="/progetti" element={<Progetti />} />
                <Route path="/progetti/:slug" element={<ProgettoDetail />} />
                <Route path="/investitori" element={<Investitori />} />
                <Route path="/operazioni" element={<Operazioni />} />
                <Route path="/operazioni/:slug" element={<OperazioneDetail />} />
                <Route path="/opportunita" element={<Navigate to="/operazioni" replace />} />
                <Route path="/proponi-immobile" element={<ProponiImmobile />} />
                <Route path="/contatti" element={<Contatti />} />
                <Route path="/la-rotta-del-valore" element={<RottaDelValore />} />
                <Route path="/diario-di-bordo" element={<DiarioDiBordo />} />
                <Route path="/diario-di-bordo/:slug" element={<DiarioPost />} />
                <Route path="/privacy-policy" element={<Legal type="privacy" />} />
                <Route path="/cookie-policy" element={<Legal type="cookie" />} />
                <Route path="/termini-e-condizioni" element={<Legal type="terms" />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<Protected />}>
                <Route index element={<Dashboard />} />
                <Route path="progetti" element={<ProjectsAdmin />} />
                <Route path="progetti/nuovo" element={<ProjectForm />} />
                <Route path="progetti/:id" element={<ProjectForm />} />
                <Route path="immagini" element={<MediaAdmin />} />
                <Route path="instagram" element={<InstagramAdmin />} />
                <Route path="operazioni" element={<OperationsAdmin />} />
                <Route path="operazioni/nuova" element={<OperationForm />} />
                <Route path="operazioni/:id" element={<OperationForm />} />
                <Route path="opportunita" element={<Navigate to="/admin/operazioni" replace />} />
                <Route path="richieste" element={<LeadsAdmin />} />
                <Route path="libro" element={<BookAdmin />} />
                <Route path="diario" element={<PostsAdmin />} />
                <Route path="diario/nuovo" element={<PostForm />} />
                <Route path="diario/:id" element={<PostForm />} />
                <Route path="impostazioni" element={<SettingsAdmin />} />
              </Route>
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" toastOptions={{ style: { borderRadius: 0, fontFamily: "Outfit" } }} />
        </AuthProvider>
      </SettingsProvider>
    </div>
  );
}

export default App;
